import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notificationEventTypeLabel } from '../i18n/notification_event_types'
import { NOTIFICATION_ROLES } from '../i18n/notification_roles'
import { useSetAllRolesEnabled } from '../hooks/useTemplates'
import { RoleRow } from './RoleRow'
import type { NotificationTemplateRow } from '@/api/notifications'

interface Props {
  eventType: string
  rows: NotificationTemplateRow[]
}

export function EventCard({ eventType, rows }: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const setAllMut = useSetAllRolesEnabled()

  const enabledCount = rows.filter((r) => r.is_enabled).length
  const totalRoles = NOTIFICATION_ROLES.length

  const rowsByRole = new Map(rows.map((r) => [r.role, r]))
  const allEnabled = enabledCount === totalRoles
  const noneEnabled = enabledCount === 0

  function handleToggleAll() {
    void setAllMut.mutateAsync({ event_type: eventType, enabled: !allEnabled })
  }

  const badgeClass = noneEnabled
    ? 'bg-[color:var(--stryzh-color-border-subtle)] text-[color:var(--stryzh-color-text-secondary)]'
    : allEnabled
      ? 'bg-emerald-900/40 text-emerald-200'
      : 'bg-amber-900/40 text-amber-200'

  const badgeText = noneEnabled ? '0' : `${enabledCount}`

  return (
    <div className="rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)]">
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={`text-[color:var(--stryzh-color-text-secondary)] transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden
        >
          ▶
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[color:var(--stryzh-color-text-primary)]">
            {notificationEventTypeLabel(eventType)}
          </p>
          <p className="break-all font-mono text-xs text-[color:var(--stryzh-color-text-secondary)]">
            {eventType}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeClass}`}
          title={t('notif.eventCard.enabledFor', { count: enabledCount, total: totalRoles })}
        >
          {noneEnabled ? t('notif.eventCard.off') : t('notif.eventCard.ofTotal', { count: badgeText, total: totalRoles })}
        </span>
      </button>

      {open && (
        <div className="border-t border-[color:var(--stryzh-color-border-subtle)] px-4 pb-4 pt-3">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
              {t('notif.eventCard.enabledFor', { count: enabledCount, total: totalRoles })}
            </span>
            <button
              type="button"
              onClick={handleToggleAll}
              disabled={setAllMut.isPending}
              className="rounded-md border border-[color:var(--stryzh-color-border-default)] px-3 py-1 text-xs text-[color:var(--stryzh-color-text-secondary)] hover:bg-[color:var(--stryzh-color-border-subtle)] disabled:opacity-50"
            >
              {allEnabled ? t('notif.eventCard.disableAll') : t('notif.eventCard.enableAll')}
            </button>
          </div>
          <div className="space-y-2">
            {NOTIFICATION_ROLES.map((role) => {
              const row = rowsByRole.get(role)
              if (!row) return null
              return <RoleRow key={role} row={row} />
            })}
          </div>
        </div>
      )}
    </div>
  )
}
